/**
 * PublicAgencyPage — Lot 1B.3
 * Page publique enrichie /p/agency/:slug — nœud du réseau local Homly.
 *
 * 6 blocs (selon cadrage fondateur) :
 * A. En-tête agence (logo, nom, suivre, partager, signaler, compteur)
 * B. Équipe filtrable (zone, spécialité) — pas de "meilleur agent"
 * C. Présence locale (zones déclarées vs vérifiées par l'activité)
 * D. Trust indicators (données réelles seulement, pas de note opaque)
 * E. Contenus (état vide propre — viendra avec Lot 1C)
 * F. CTA vendeur orienté besoin (sell / estimate / find_agent / question / callback)
 *
 * JSON-LD Organization + RealEstateAgent injecté dans <head>.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getToken } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    Users, MapPin, Share2, Flag, Building2, BadgeCheck, ShieldCheck,
    Calendar, Loader2, UserPlus, UserCheck, ChevronRight, Sparkles,
    Home, Calculator, Search, MessageCircleQuestion, Phone, Mail
} from 'lucide-react';

const CTA_OPTIONS = [
    { value: 'sell_property', label: 'Vendre un bien', icon: Home, hint: 'Démarrer un projet de vente' },
    { value: 'estimate', label: 'Faire estimer un bien', icon: Calculator, hint: 'Connaître la valeur de mon bien' },
    { value: 'find_agent', label: 'Trouver un agent', icon: Search, hint: 'Un agent dans ma zone' },
    { value: 'ask_question', label: 'Poser une question', icon: MessageCircleQuestion, hint: 'Question immobilière locale' },
    { value: 'callback', label: 'Être rappelé', icon: Phone, hint: 'Un agent me contacte' },
];

const REPORT_REASONS = [
    { value: 'fake_agency', label: 'Agence faux ou usurpé' },
    { value: 'inappropriate_content', label: 'Contenu inapproprié' },
    { value: 'wrong_info', label: 'Informations incorrectes' },
    { value: 'spam', label: 'Spam ou publicité' },
    { value: 'other', label: 'Autre raison' },
];

export default function PublicAgencyPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [following, setFollowing] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [zoneFilter, setZoneFilter] = useState('');
    const [specialtyFilter, setSpecialtyFilter] = useState('');
    const [reportOpen, setReportOpen] = useState(false);
    const [ctaOpen, setCtaOpen] = useState(false);
    const [ctaType, setCtaType] = useState('sell_property');
    const [ctaZone, setCtaZone] = useState('');
    const [ctaEmail, setCtaEmail] = useState('');
    const [ctaMessage, setCtaMessage] = useState('');
    const [ctaBusy, setCtaBusy] = useState(false);
    const [reportReason, setReportReason] = useState('fake_agency');
    const [reportMessage, setReportMessage] = useState('');
    const [reportBusy, setReportBusy] = useState(false);
    const authed = !!getToken();

    useEffect(() => {
        if (!slug) return;
        loadAgency();
        if (authed) loadFollowStatus();
    }, [slug, authed]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!data?.json_ld) return;
        const id = 'homly-agency-jsonld';
        let el = document.getElementById(id);
        if (!el) {
            el = document.createElement('script');
            el.id = id;
            el.type = 'application/ld+json';
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data.json_ld);
        document.title = `${data.name} — Agence immobilière${data.city ? ' à ' + data.city : ''} | Homly Réseau`;
        const setMeta = (attr, key, value) => {
            let m = document.querySelector(`meta[${attr}="${key}"]`);
            if (!m) { m = document.createElement('meta'); m.setAttribute(attr, key); document.head.appendChild(m); }
            m.setAttribute('content', value);
        };
        const desc = `${data.description || data.name + ' — agence immobilière'}${data.city ? ' à ' + data.city : ''}. ${data.team?.length || 0} agents. Suivez l'activité locale sur Homly Réseau.`.slice(0, 160);
        setMeta('name', 'description', desc);
        setMeta('property', 'og:title', `${data.name} — Homly Réseau`);
        setMeta('property', 'og:description', desc);
        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:url', window.location.href);
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
        canonical.href = window.location.href;
        return () => { const e2 = document.getElementById(id); if (e2) e2.remove(); };
    }, [data]);

    const loadAgency = async () => {
        try {
            const { data: payload } = await api.get(`/public/agency/${slug}/reseau`);
            setData(payload);
        } catch (e) {
            toast.error('Agence non trouvée');
        } finally {
            setLoading(false);
        }
    };

    const loadFollowStatus = async () => {
        try {
            const { data: s } = await api.get(`/agencies/${slug}/follow-status`);
            setFollowing(!!s.following);
        } catch (e) { /* silent */ }
    };

    const toggleFollow = async () => {
        if (!authed) {
            toast.info('Connectez-vous pour suivre cette agence', {
                action: { label: 'Se connecter', onClick: () => navigate('/login') },
            });
            return;
        }
        setFollowBusy(true);
        try {
            if (following) {
                await api.delete(`/agencies/${slug}/follow`);
                setFollowing(false);
                setData((d) => d && ({ ...d, counters: { ...d.counters, followers_count: Math.max(0, (d.counters?.followers_count || 1) - 1) } }));
            } else {
                await api.post(`/agencies/${slug}/follow`);
                setFollowing(true);
                setData((d) => d && ({ ...d, counters: { ...d.counters, followers_count: (d.counters?.followers_count || 0) + 1 } }));
            }
        } catch (e) {
            toast.error('Erreur suivi');
        } finally {
            setFollowBusy(false);
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({ title: document.title, url });
                api.post(`/agencies/${slug}/share`, { channel: 'native_share' }).catch(() => {});
            } else {
                await navigator.clipboard.writeText(url);
                toast.success('Lien copié');
                api.post(`/agencies/${slug}/share`, { channel: 'copy_link' }).catch(() => {});
            }
        } catch (e) { /* cancelled */ }
    };

    const submitReport = async () => {
        setReportBusy(true);
        try {
            await api.post(`/agencies/${slug}/report`, { reason: reportReason, message: reportMessage || null });
            toast.success('Signalement envoyé');
            setReportOpen(false); setReportReason('fake_agency'); setReportMessage('');
        } catch { toast.error('Erreur signalement'); } finally { setReportBusy(false); }
    };

    const submitCTA = async () => {
        if (!ctaEmail && !data?.email) {
            toast.error('Email requis pour vous recontacter');
            return;
        }
        setCtaBusy(true);
        try {
            await api.post(`/agencies/${slug}/seller-cta`, {
                cta_type: ctaType,
                zone: ctaZone || null,
                contact_email: ctaEmail || null,
                message: ctaMessage || null,
            });
            toast.success("Merci, l'agence vous recontactera rapidement");
            setCtaOpen(false); setCtaZone(''); setCtaEmail(''); setCtaMessage('');
        } catch { toast.error('Erreur envoi'); } finally { setCtaBusy(false); }
    };

    const openAgent = (slugAgent) => {
        api.post(`/agencies/${slug}/track-click`, { agent_slug: slugAgent }).catch(() => {});
        navigate(`/p/agent/${slugAgent}`);
    };

    const filteredTeam = useMemo(() => {
        if (!data) return [];
        return data.team.filter((t) => {
            if (zoneFilter) {
                const zones = (t.professional_zones || []).map((z) => z.label?.toLowerCase() || '');
                if (!zones.some((z) => z.includes(zoneFilter.toLowerCase()))) return false;
            }
            if (specialtyFilter) {
                if (!(t.specialties || []).some((s) => s.toLowerCase().includes(specialtyFilter.toLowerCase()))) return false;
            }
            return true;
        });
    }, [data, zoneFilter, specialtyFilter]);

    if (loading) {
        return (
            <div data-reseau="light" className="reseau-light min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--reseau-violet)' }} />
            </div>
        );
    }
    if (!data) {
        return <div data-reseau="light" className="reseau-light min-h-screen flex items-center justify-center" style={{ color: 'var(--reseau-ink)' }}>Agence introuvable</div>;
    }

    const t = data.trust_indicators;
    return (
        <div data-reseau="light" className="reseau-light min-h-screen" data-testid="public-agency-page">
            {/* A. En-tête */}
            <header className="border-b" style={{ background: 'var(--reseau-bg-soft)', borderColor: 'var(--reseau-border)' }}>
                <div className="mx-auto px-4 sm:px-6 py-8 sm:py-10" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                    <div className="flex items-start gap-6 flex-wrap">
                        <div className="w-20 h-20 rounded-xl flex items-center justify-center"
                             style={{ background: 'var(--reseau-violet-soft)', border: '1px solid var(--reseau-border)' }}>
                            {data.logo_url
                                ? <img src={data.logo_url} alt={data.name} className="w-full h-full object-cover rounded-xl" />
                                : <Building2 className="w-10 h-10" style={{ color: 'var(--reseau-violet)' }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="reseau-h1 text-2xl sm:text-3xl flex items-center gap-2 flex-wrap" data-testid="agency-name">
                                {data.name}
                                {t.agency_verified && (
                                    <Badge className="reseau-badge reseau-badge-success">
                                        <BadgeCheck className="w-3 h-3 mr-1" /> Vérifiée
                                    </Badge>
                                )}
                            </h1>
                            <div className="text-sm mt-1 flex items-center gap-3 flex-wrap" style={{ color: 'var(--reseau-ink-muted)' }}>
                                {data.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {data.city}</span>}
                                <span className="flex items-center gap-1" data-testid="agency-followers">
                                    <Users className="w-3.5 h-3.5" /> {data.counters?.followers_count || 0} abonnés
                                </span>
                                {t.seniority_days_on_homly !== null && (
                                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {Math.floor(t.seniority_days_on_homly / 30)} mois sur Homly</span>
                                )}
                            </div>
                            {data.description && <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: 'var(--reseau-ink-soft)' }}>{data.description}</p>}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <Button onClick={toggleFollow} disabled={followBusy} data-testid="agency-follow-button"
                                className={following ? 'reseau-btn reseau-btn-ghost' : 'reseau-btn reseau-btn-primary'}>
                                {followBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : following ? <><UserCheck className="w-4 h-4 mr-1.5" /> Suivi</> : <><UserPlus className="w-4 h-4 mr-1.5" /> Suivre</>}
                            </Button>
                            <Button onClick={handleShare} variant="ghost" data-testid="agency-share-button" className="reseau-btn reseau-btn-ghost">
                                <Share2 className="w-4 h-4 mr-1.5" /> Partager
                            </Button>
                            <Button onClick={() => setReportOpen(true)} variant="ghost" data-testid="agency-report-button" className="reseau-btn-text" style={{ color: 'var(--reseau-ink-muted)' }}>
                                <Flag className="w-4 h-4 mr-1.5" /> Signaler
                            </Button>
                        </div>
                    </div>
                    {/* CTA vendeur */}
                    <div className="mt-6 p-4 rounded-xl flex items-center gap-3 flex-wrap"
                         style={{ background: 'var(--reseau-violet-soft)', border: '1px solid var(--reseau-border-violet)' }}
                         data-testid="agency-cta-banner">
                        <Sparkles className="w-5 h-5" style={{ color: 'var(--reseau-orange)' }} />
                        <div className="flex-1 min-w-[200px]">
                            <div className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>Vous êtes vendeur ou en projet ?</div>
                            <div className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>Indiquez votre besoin, un agent local vous recontactera</div>
                        </div>
                        <Button onClick={() => setCtaOpen(true)} data-testid="agency-seller-cta-button" className="reseau-btn reseau-btn-accent">
                            Présenter mon projet <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </header>

            <div className="mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-8" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <div className="lg:col-span-2 space-y-10">
                    {/* B. Équipe */}
                    <section data-testid="agency-team-section">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <h2 className="reseau-h2 text-lg">Équipe ({data.team?.length || 0})</h2>
                            <div className="flex gap-2">
                                <Input placeholder="Filtrer par zone" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} data-testid="team-filter-zone" className="reseau-input h-9 w-40 text-sm" />
                                <Input placeholder="Spécialité" value={specialtyFilter} onChange={(e) => setSpecialtyFilter(e.target.value)} data-testid="team-filter-specialty" className="reseau-input h-9 w-40 text-sm" />
                            </div>
                        </div>
                        {filteredTeam.length === 0 ? (
                            <div className="reseau-empty" data-testid="team-empty">
                                {data.team?.length === 0 ? 'Aucun agent publié pour le moment.' : 'Aucun agent ne correspond aux filtres.'}
                            </div>
                        ) : (
                            <div className="grid sm:grid-cols-2 gap-3">
                                {filteredTeam.map((t) => (
                                    <button key={t.id} onClick={() => openAgent(t.slug)} data-testid={`team-agent-${t.slug}`}
                                        className="text-left p-4 reseau-card reseau-card-hoverable flex gap-3">
                                        <div className="w-12 h-12 rounded-full flex items-center justify-center font-semibold flex-shrink-0 overflow-hidden"
                                             style={{ background: 'linear-gradient(135deg, var(--reseau-violet-soft), var(--reseau-orange-soft))',
                                                      color: 'var(--reseau-violet)' }}>
                                            {t.photo_url ? <img src={t.photo_url} alt="" className="w-full h-full rounded-full object-cover" /> : `${(t.first_name?.[0] || '?')}${(t.last_name?.[0] || '')}`}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold flex items-center gap-1 truncate" style={{ color: 'var(--reseau-ink)' }}>
                                                {t.first_name} {t.last_name}
                                                {t.is_verified_identity && <BadgeCheck className="w-3.5 h-3.5" style={{ color: 'var(--reseau-success)' }} />}
                                            </div>
                                            <div className="text-xs truncate" style={{ color: 'var(--reseau-ink-muted)' }}>{t.title_pro}</div>
                                            {t.specialties?.length > 0 && (
                                                <div className="text-xs mt-1 truncate" style={{ color: 'var(--reseau-ink-faint)' }}>{t.specialties.slice(0, 2).join(' · ')}</div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* E. Contenus */}
                    <section data-testid="agency-content-section">
                        <h2 className="reseau-h2 text-lg mb-4">Contenus de l'agence</h2>
                        <div className="reseau-empty" data-testid="content-empty">
                            <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--reseau-orange)' }} />
                            <h3>Pas encore de contenu publié</h3>
                            <p>Les publications de l'équipe apparaîtront ici dès que les agents publieront du contenu local.</p>
                        </div>
                    </section>
                </div>

                <aside className="space-y-6">
                    {/* C. Présence locale */}
                    <section data-testid="agency-zones-section" className="reseau-card p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--reseau-ink-muted)', letterSpacing: '0.05em' }}>Présence locale</h3>
                        {data.structured_zones?.length === 0 ? (
                            <div className="text-xs italic" style={{ color: 'var(--reseau-ink-muted)' }}>Zones d'intervention non encore documentées.</div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {data.structured_zones.map((z, i) => (
                                    <button key={i}
                                        onClick={() => { api.post(`/agencies/${slug}/track-click`, { zone_label: z.label }).catch(() => {}); }}
                                        data-testid={`zone-chip-${i}`}
                                        title={z.verified ? 'Zone vérifiée par l\'activité des agents' : 'Zone déclarée'}
                                        className={z.verified ? 'reseau-badge reseau-badge-success' : 'reseau-badge reseau-badge-neutral'}>
                                        {z.verified && <BadgeCheck className="w-3 h-3 mr-1" />}
                                        {z.label}{z.postal_code ? ` · ${z.postal_code}` : ''}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="text-[10px] mt-3" style={{ color: 'var(--reseau-ink-faint)' }}>Adresse physique ≠ zones d'intervention</div>
                    </section>

                    {/* D. Trust indicators */}
                    <section data-testid="agency-trust-section" className="reseau-card p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: 'var(--reseau-ink-muted)', letterSpacing: '0.05em' }}>
                            <ShieldCheck className="w-3.5 h-3.5" /> Indicateurs de confiance
                        </h3>
                        <ul className="space-y-2 text-sm">
                            <TrustItem ok={t.agency_verified} label="Agence vérifiée" />
                            <TrustItem ok={t.published_agents_count > 0} label={`${t.published_agents_count} agent${t.published_agents_count > 1 ? 's' : ''} publié${t.published_agents_count > 1 ? 's' : ''}`} />
                            <TrustItem ok={t.verified_identities_count > 0} label={`${t.verified_identities_count} identité${t.verified_identities_count > 1 ? 's' : ''} vérifiée${t.verified_identities_count > 1 ? 's' : ''}`} />
                            <TrustItem ok={t.avg_team_completeness_percent >= 60} label={`Complétude moyenne de l'équipe : ${t.avg_team_completeness_percent}%`} />
                            {t.seniority_days_on_homly !== null && (
                                <TrustItem ok label={`Sur Homly depuis ${Math.floor(t.seniority_days_on_homly / 30)} mois`} />
                            )}
                            <li className="text-[11px] italic pt-1" style={{ color: 'var(--reseau-ink-faint)' }}>Avis vérifiés, recommandations et temps de réponse disponibles prochainement.</li>
                        </ul>
                    </section>
                </aside>
            </div>

            {/* CTA Dialog */}
            <Dialog open={ctaOpen} onOpenChange={setCtaOpen}>
                <DialogContent data-reseau="light" className="reseau-light bg-white" data-testid="cta-dialog">
                    <DialogHeader><DialogTitle className="reseau-h2 text-xl">Présenter mon projet</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label style={{ color: 'var(--reseau-ink-soft)' }}>Mon besoin</Label>
                            <div className="grid grid-cols-1 gap-1.5">
                                {CTA_OPTIONS.map((opt) => {
                                    const Icon = opt.icon;
                                    const active = ctaType === opt.value;
                                    return (
                                        <label key={opt.value}
                                               className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
                                               style={{
                                                   borderColor: active ? 'var(--reseau-violet)' : 'var(--reseau-border)',
                                                   background: active ? 'var(--reseau-violet-soft)' : 'var(--reseau-surface)',
                                               }}
                                               data-testid={`cta-option-${opt.value}`}>
                                            <input type="radio" name="cta" value={opt.value} checked={active} onChange={() => setCtaType(opt.value)} className="accent-purple-700" />
                                            <Icon className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                                            <div className="flex-1">
                                                <div className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>{opt.label}</div>
                                                <div className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>{opt.hint}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input placeholder="Zone (ex: Lyon 3)" value={ctaZone} onChange={(e) => setCtaZone(e.target.value)} data-testid="cta-zone" className="reseau-input" />
                            <Input type="email" placeholder="Votre email" value={ctaEmail} onChange={(e) => setCtaEmail(e.target.value)} data-testid="cta-email" className="reseau-input" />
                        </div>
                        <Textarea rows={3} placeholder="Précisez votre besoin (optionnel)" value={ctaMessage} onChange={(e) => setCtaMessage(e.target.value)} data-testid="cta-message" className="reseau-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCtaOpen(false)} data-testid="cta-cancel" className="reseau-btn reseau-btn-ghost">Annuler</Button>
                        <Button onClick={submitCTA} disabled={ctaBusy} data-testid="cta-submit" className="reseau-btn reseau-btn-primary">
                            {ctaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer ma demande'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Report Dialog */}
            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                <DialogContent data-reseau="light" className="reseau-light bg-white" data-testid="agency-report-dialog">
                    <DialogHeader><DialogTitle className="reseau-h2 text-xl">Signaler cette agence</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        {REPORT_REASONS.map((r) => (
                            <label key={r.value}
                                   className="flex items-center gap-2 p-2 rounded cursor-pointer"
                                   style={{ background: 'var(--reseau-bg-soft)' }}
                                   data-testid={`agency-report-reason-${r.value}`}>
                                <input type="radio" name="ar" value={r.value} checked={reportReason === r.value} onChange={() => setReportReason(r.value)} className="accent-purple-700" />
                                <span className="text-sm" style={{ color: 'var(--reseau-ink-soft)' }}>{r.label}</span>
                            </label>
                        ))}
                        <Textarea rows={3} value={reportMessage} onChange={(e) => setReportMessage(e.target.value)} placeholder="Détails (optionnel)" data-testid="agency-report-message" className="reseau-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setReportOpen(false)} data-testid="agency-report-cancel" className="reseau-btn reseau-btn-ghost">Annuler</Button>
                        <Button onClick={submitReport} disabled={reportBusy} data-testid="agency-report-submit"
                                className="reseau-btn"
                                style={{ background: 'var(--reseau-danger)', color: '#fff' }}>
                            {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TrustItem({ ok, label }) {
    return (
        <li className="flex items-center gap-2" style={{ color: 'var(--reseau-ink-soft)' }}>
            <div className="w-1.5 h-1.5 rounded-full"
                 style={{ background: ok ? 'var(--reseau-success)' : 'var(--reseau-ink-faint)' }} />
            <span style={{ color: ok ? 'var(--reseau-ink)' : 'var(--reseau-ink-muted)' }}>{label}</span>
        </li>
    );
}
